import React, { useState, useEffect, useRef } from 'react';
import { Search, Star, AlertCircle, Info } from 'lucide-react';
import { GlossaryTerm, GlossarySourceCategory } from '../../types/glossary';
import { glossaryService } from '../../services/glossaryService';
import { glossaryCategoryService } from '../../services/glossaryCategoryService';
import { useAuth } from '../../hooks/useAuth';
import { TermOfTheDay } from './TermOfTheDay';
import { ChromeExtension } from './ChromeExtension';
import { CategorySelector } from './CategorySelector';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { toast } from '../ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';

/**
 * Ultra-compact component for displaying glossary terms with hover tooltips
 */
export const TooltipGlossaryList: React.FC = () => {
  const { user } = useAuth();
  const [terms, setTerms] = useState<GlossaryTerm[]>([]);
  const [filteredTerms, setFilteredTerms] = useState<GlossaryTerm[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [favoritedTerms, setFavoritedTerms] = useState<Record<string, boolean>>({});
  const [totalTerms, setTotalTerms] = useState(0);
  const [hoveredTerm, setHoveredTerm] = useState<GlossaryTerm | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [categories, setCategories] = useState<GlossarySourceCategory[]>([]);
  const [selectedTerm, setSelectedTerm] = useState<GlossaryTerm | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch categories for name mapping
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const allCategories = await glossaryCategoryService.getActiveCategories();
        setCategories(allCategories);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();
  }, []);

  // Fetch all glossary terms
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setLoading(true);
        const allTerms = await glossaryService.getGlossaryTerms();
        setTerms(allTerms);
        setFilteredTerms(allTerms);
        setTotalTerms(allTerms.length);
        setError(null);
      } catch (err) {
        console.error('Error fetching glossary terms:', err);
        setError('Failed to load glossary terms');
      } finally {
        setLoading(false);
      }
    };

    fetchTerms();
  }, []);

  // Check which terms are favorited
  useEffect(() => {
    const checkFavoritedTerms = async () => {
      if (!user || terms.length === 0) return;

      try {
        const favorites = await glossaryService.getFavorites(user.uid);
        const favoritedMap: Record<string, boolean> = {};
        terms.forEach(term => {
          favoritedMap[term.id] = favorites.some(fav => fav.termId === term.id);
        });
        setFavoritedTerms(favoritedMap);
      } catch (err) {
        console.error('Error checking favorited terms:', err);
      }
    };

    checkFavoritedTerms();
  }, [user, terms]);

  // Filter terms based on search query and selected category
  useEffect(() => {
    let filtered = terms;

    if (selectedCategoryId) {
      filtered = filtered.filter(term => term.categoryId === selectedCategoryId);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(term =>
        term.term.toLowerCase().includes(query) ||
        term.content.toLowerCase().includes(query)
      );
    }
    
    setFilteredTerms(filtered);
  }, [searchQuery, selectedCategoryId, terms]);

  // Toggle term favorite status
  const toggleFavorite = async (termId: string) => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to manage favorites",
        variant: "destructive",
      });
      return;
    }

    try {
      const isFavorited = favoritedTerms[termId];
      
      if (isFavorited) {
        await glossaryService.removeFavorite(termId, user.uid);
        setFavoritedTerms(prev => ({ ...prev, [termId]: false }));
        toast({
          title: "Removed from Favorites",
          description: "Term has been removed from your favorites",
          variant: "success",
          className: "bg-green-600 text-white border-green-700",
          duration: 2000,
        });
      } else {
        const categories = await glossaryService.getCategories(user.uid);
        let defaultCategory = categories.find(cat => cat.name === 'My Favorites');
        
        if (!defaultCategory) {
          const categoryId = await glossaryService.createCategory(
            'My Favorites',
            user.uid,
            user.displayName || 'Unknown User',
            user.photoURL || undefined
          );
          const updatedCategories = await glossaryService.getCategories(user.uid);
          defaultCategory = updatedCategories.find(cat => cat.id === categoryId);
          
          if (!defaultCategory) {
            throw new Error('Failed to create default category');
          }
        }
        
        await glossaryService.addToFavorites(termId, user.uid, defaultCategory.id);
        setFavoritedTerms(prev => ({ ...prev, [termId]: true }));
        toast({
          title: "Added to Favorites",
          description: "Term has been added to your favorites",
          variant: "success",
          className: "bg-green-600 text-white border-green-700",
          duration: 2000,
        });
      }
    } catch (error) {
      console.error('Error toggling favorite status:', error);
      toast({
        title: "Error",
        description: "Failed to update favorites",
        variant: "destructive",
        className: "bg-red-600 text-white border-red-700",
        duration: 2000,
      });
    }
  };

  // Get category name from ID
  const getCategoryName = (categoryId?: string): string => {
    if (!categoryId) return 'General';
    
    // First try to use the categoryName if it exists
    const term = terms.find(t => t.categoryId === categoryId);
    if (term?.categoryName) return term.categoryName;
    
    // Otherwise look up in categories array
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || categoryId;
  };

  // Open term definition modal
  const openTermDialog = (term: GlossaryTerm) => {
    setSelectedTerm(term);
    setIsDialogOpen(true);
    setHoveredTerm(null); // Hide tooltip when opening modal
  };

  // Handle term hover for tooltip with better positioning
  const handleTermHover = (term: GlossaryTerm, event: React.MouseEvent) => {
    // Clear any existing timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setHoveredTerm(term);
  };

  const handleTermLeave = () => {
    // Set a timeout to hide the tooltip, but allow it to be cancelled
    hideTimeoutRef.current = setTimeout(() => {
      setHoveredTerm(null);
      hideTimeoutRef.current = null;
    }, 300); // Increased delay to 300ms for better UX
  };

  // Handle tooltip hover to keep it visible
  const handleTooltipEnter = () => {
    // Cancel the hide timeout when hovering over tooltip
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleTooltipLeave = () => {
    // Immediately hide when leaving tooltip
    setHoveredTerm(null);
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Strip HTML tags for tooltip preview
  const getTextPreview = (html: string, maxLength: number = 200) => {
    const text = html.replace(/<[^>]*>/g, '');
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <div className="px-4 md:px-8">
      {/* Featured components in a grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <TermOfTheDay />
        <ChromeExtension />
      </div>
      
      {/* Glossary Terms List */}
      <div className="mb-4">
        {/* Category Selector and Search bar */}
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <CategorySelector
                selectedCategoryId={selectedCategoryId}
                onCategoryChange={setSelectedCategoryId}
              />
              <div className="text-gray-800 dark:text-gray-300">
                {selectedCategoryId
                  ? `${filteredTerms.length} ${getCategoryName(selectedCategoryId)} terms`
                  : `${totalTerms} total terms available`
                }
              </div>
            </div>
            
            <div className="relative flex-1 md:max-w-md">
              <Input
                type="text"
                placeholder="Search glossary terms..."
                className="pr-10 glossary-search bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400"
                style={{
                  border: '2px solid var(--glossary-border)',
                  boxShadow: '0 0 0 1px rgba(99, 102, 241, 0.1)'
                }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <Search className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Error state */}
        {error && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-400">{error}</p>
            </div>
          </div>
        )}
        
        {/* Loading state */}
        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
            {[...Array(20)].map((_, i) => (
              <Skeleton key={i} className="h-8 rounded" />
            ))}
          </div>
        )}
        
        {/* No results */}
        {!loading && filteredTerms.length === 0 && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 text-center">
            <p className="text-gray-400">
              {searchQuery 
                ? `No terms found matching "${searchQuery}"`
                : "No glossary terms available"}
            </p>
          </div>
        )}
        
        {/* Ultra-Compact Terms Grid */}
        {!loading && filteredTerms.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-1">
            {filteredTerms.map((term) => (
              <div
                key={term.id}
                className="relative group"
                onMouseEnter={(e) => handleTermHover(term, e)}
                onMouseLeave={handleTermLeave}
              >
                <div
                  className="flex items-center bg-gray-100 dark:bg-gray-800/50 hover:bg-gray-200 dark:hover:bg-gray-700/70 border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 rounded-lg p-2 cursor-pointer"
                  onClick={() => openTermDialog(term)}
                >
                  {/* Favorite Star */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(term.id);
                    }}
                    className="h-6 w-6 p-0 mr-2 hover:bg-gray-200 dark:hover:bg-gray-600 flex-shrink-0"
                  >
                    <Star
                      className={`h-3 w-3 ${
                        favoritedTerms[term.id]
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-500 dark:text-gray-400 hover:text-yellow-400'
                      }`}
                    />
                  </Button>
                  
                  {/* Term Name */}
                  <span className="text-sm text-gray-800 dark:text-white truncate flex-1 font-medium">
                    {term.term}
                  </span>
                  
                  {/* Category Indicator */}
                  <div className="w-2 h-2 bg-indigo-500 rounded-full ml-2 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tooltip */}
      {hoveredTerm && (
        <div
          className="fixed z-50 max-w-sm p-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translate(-50%, -100%)',
          }}
          onMouseEnter={handleTooltipEnter}
          onMouseLeave={handleTooltipLeave}
          onClick={() => openTermDialog(hoveredTerm)}
        >
          <div className="flex items-center gap-2 mb-2">
            <Info className="h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0" />
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{hoveredTerm.term}</h3>
          </div>
          <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
            {getTextPreview(hoveredTerm.content)}
          </p>
          <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
            Category: {getCategoryName(hoveredTerm.categoryId)}
          </div>
          <div className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-medium">
            Click to view full definition
          </div>
        </div>
      )}

      {/* Term Definition Modal */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500 dark:text-blue-400" />
              {selectedTerm?.term}
            </DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Category: {selectedTerm ? getCategoryName(selectedTerm.categoryId) : 'General'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            <div
              className="prose dark:prose-invert max-w-none prose-sm text-gray-700 dark:text-gray-300"
              dangerouslySetInnerHTML={{
                __html: selectedTerm?.content.replace(/<\/p><p>/g, '</p><br><p>') || ''
              }}
            />
          </div>
          
          <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-300 dark:border-gray-700">
            <Button
              variant="outline"
              onClick={() => selectedTerm && toggleFavorite(selectedTerm.id)}
              className="flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Star
                className={`h-4 w-4 ${
                  selectedTerm && favoritedTerms[selectedTerm.id]
                    ? 'text-yellow-400 fill-yellow-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              />
              {selectedTerm && favoritedTerms[selectedTerm.id] ? 'Remove from Favorites' : 'Add to Favorites'}
            </Button>
            
            <Button
              onClick={() => setIsDialogOpen(false)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};